import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BackButtonComponent from '../../../components/BackButtonComponent/BackButtonComponent';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { PrimaryButton } from '../../../styledComponents/buttons';
import {
  CommonText,
  Container,
  ContentHeader,
  InputWithLabelSection,
  PageTitle,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';
import CreateEditTaskForm from '../create-editTaskForm';
import CheckList from './checkList';
import CheckLists from './checkLists';
import { InputSection } from './createTaskStyles';

const CreateTask = ({
  isLoading,
  orgMembersList,
  addTaskLocationLoading,
  createdTaskInfo,
  locationsList,
  createdTaskLocationInfo,
  memberShortListIsLoading,
  getOrganizationMembersList,
  createTask,
  getTaskLocation,
  addTaskLocation,
  officesShortList,
  getOfficesShortList,
  getTaskType,
  taskTypeIsLoading,
  taskTypeList,
  history,
  getPublicZoneList,
  publicZoneList,
  getDefaultSubTasks,
  defaultSubTasks,
  newDefaultSubTask,
  isAddDefaultSubTaskLoading,
  addDefaultSubTask,
  clearNewDefaultSubTask,
  masterTasksList,
  getMasterTasks,
}) => {
  const { t, i18n } = useTranslation();

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState('');
  const [officeOptions, setOfficeOptions] = useState([]);
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);
  const [defaultCheckListOptions, setDefaultCheckListOptions] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [taskDescription, setTaskDescription] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [taskLocationOptions, setTaskLocationOptions] = useState([]);
  const [startDate, setStartDate] = useState(
    moment(new Date()).add(5, 'minutes'),
  );
  const [endDate, setEndDate] = useState(moment(new Date()).add(65, 'minutes'));
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);

  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [20, 50, 100];
  const [statusOptions, setStatusOptions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [checklistData, setChecklistData] = useState([]);

  useEffect(() => {
    getTaskType();
    getPublicZoneList();
    getTaskLocation();
  }, []);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      setSelectedOffice({
        label: officesShortList[0].name,
        value: officesShortList[0].id,
      });
      let newArr = [];
      officesShortList.forEach(office => {
        newArr.push({
          label: office.name,
          value: office.id,
        });
      });
      setOfficeOptions(newArr);
    } else {
      setOfficeOptions([]);
      setSelectedOffice('');
      setTaskLocationOptions([]);
      setSelectedLocation('');
    }
  }, [officesShortList]);

  useEffect(() => {
    if (selectedOffice) {
      if (selectedPublicZone) {
        getDefaultSubTasks({
          public_zone_id: selectedPublicZone.value,
          office_id: selectedOffice.value,
        });
      }
      getOrganizationMembersList({ office_id: selectedOffice.value });
      getMasterTasks({
        related_office: selectedOffice.value,
        public_zone: selectedPublicZone.value,
      });
    }
  }, [selectedOffice]);

  useEffect(() => {
    if (
      selectedOffice &&
      selectedPublicZone &&
      locationsList &&
      locationsList.length > 0
    ) {
      const filteredLocations = locationsList.filter(location => {
        const matchesOffice =
          selectedOffice.value === null ||
          location.office_id === selectedOffice.value;
        const matchesPublicZone =
          location.public_zone === selectedPublicZone.value;

        return matchesOffice && matchesPublicZone;
      });

      const newLocationOptions = filteredLocations.map(location => ({
        label: location.name,
        value: location.id,
        ...location,
      }));

      setTaskLocationOptions(newLocationOptions);

      if (
        newLocationOptions.length > 0 &&
        (!selectedLocation ||
          !newLocationOptions.some(
            option => option.value === selectedLocation.value,
          ))
      ) {
        setSelectedLocation(newLocationOptions[0]);
      } else if (newLocationOptions.length === 0) {
        setSelectedLocation('');
      }
    } else {
      setTaskLocationOptions([]);
      setSelectedLocation('');
    }
  }, [selectedOffice, selectedPublicZone, locationsList]);

  useEffect(() => {
    const options = masterTasksList
      ?.filter(t => t.public_zone === selectedPublicZone?.value)
      .map(task => {
        return {
          ...task,
          label: task.name,
          value: task.id,
        };
      });
    setMasterTaskOptions(options);
  }, [masterTasksList]);

  useEffect(() => {
    if (taskTypeList && taskTypeList.length > 0) {
      let newArr = [];
      if (i18n.language === 'en') {
        taskTypeList.forEach(type => {
          newArr.push({
            label: type.name,
            value: type.id,
          });
        });
      } else {
        taskTypeList.forEach(type => {
          newArr.push({
            label: type.arabic_name,
            value: type.id,
          });
        });
      }
      setStatusOptions(newArr);
    }
  }, [taskTypeList]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      let newArr = [];
      publicZoneList.forEach(zone => {
        newArr.push({
          label: zone.name,
          value: zone.id,
        });
      });
      newArr.length > 0 && setSelectedPublicZone(newArr[0]);
      setPublicZoneOptions(newArr);
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone.value) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    }
  }, [selectedPublicZone]);

  useEffect(() => {
    if (defaultSubTasks) {
      let newArr = [];
      defaultSubTasks.forEach(subTask => {
        newArr.push({
          name: subTask.name,
          id: subTask.id,
          default_sub_task_id: subTask.id,
        });
      });
      setDefaultCheckListOptions(newArr);
    }
  }, [defaultSubTasks]);

  useEffect(() => {
    if (createdTaskInfo && isSubmitted) {
      history.push({
        pathname: '/user/tasks',
        state: { from: 'create' },
      });
    }
  }, [createdTaskInfo]);

  useEffect(() => {
    if (!selectedOffice || !selectedPublicZone) {
      setSelectedAssignees([]);
    } else if (orgMembersList && orgMembersList.length > 0) {
      // Find members whose memberships match both the selected office and public zone
      const matchingMembers = orgMembersList.filter(member =>
        member.memberships?.some(
          membership =>
            membership.office_id === selectedOffice.value &&
            membership.public_zone_id === selectedPublicZone.value,
        ),
      );

      if (matchingMembers.length > 0) {
        // Find employees (first priority)
        const employees = matchingMembers.filter(member =>
          member.memberships?.some(
            membership =>
              membership.office_id === selectedOffice.value &&
              membership.public_zone_id === selectedPublicZone.value &&
              membership.role?.toLowerCase() === 'employee',
          ),
        );

        // Find supervisors (second priority)
        const supervisors = matchingMembers.filter(member =>
          member.memberships?.some(
            membership =>
              membership.office_id === selectedOffice.value &&
              membership.public_zone_id === selectedPublicZone.value &&
              membership.role?.toLowerCase() === 'supervisor',
          ),
        );

        // Select member based on priority: employee > supervisor > any matching member
        let selectedMember;
        if (employees.length > 0) {
          selectedMember = employees[0]; // First priority: employee
        } else if (supervisors.length > 0) {
          selectedMember = supervisors[0]; // Second priority: supervisor
        } else {
          selectedMember = matchingMembers[0]; // Last resort: any matching member
        }

        setSelectedAssignees([
          {
            id: selectedMember.id,
            name: selectedMember.name,
            avatar: selectedMember.avatar,
            phone: selectedMember.phone,
          },
        ]);
      } else {
        setSelectedAssignees([]);
      }
    } else if (orgMembersList && orgMembersList.length === 0) {
      setSelectedAssignees([]);
    }
  }, [orgMembersList, selectedOffice, selectedPublicZone]);

  const changeZone = publicZone => {
    officesShortList?.filter(office => office.ou);
  };

  const [masterTaskOptions, setMasterTaskOptions] = useState([]);

  const onTaskNameChange = selectedOption => {
    let errors = { ...saveErrors };
    delete errors['selectedTask'];
    setSaveErrors(errors);
    if (
      selectedOption &&
      !masterTaskOptions?.some(option => option.label === selectedOption?.label)
    ) {
      setMasterTaskOptions([...masterTaskOptions, selectedOption]);
    }
    setSelectedTask(selectedOption);
  };

  const [isSameLocationWarningModalOpen, setIsSameLocationWarningModalOpen] =
    useState(false);
  const popupToggleSameLocationWarningModal = () => {
    setIsSameLocationWarningModalOpen(!isSameLocationWarningModalOpen);
  };

  const onOfficeChange = e => {
    setSelectedOffice(e);
  };

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
  };

  const approvalToggle = () => {
    setRequiresApproval(!requiresApproval);
  };

  const handleAssignOrRemoveMember = (user, action) => {
    if (user && user.id) {
      let newArr = [...selectedAssignees];
      if (action === 'remove') {
        newArr = newArr.filter(member => member.id !== user.id);
      } else {
        newArr.push(user);
      }
      setSelectedAssignees(newArr);
    }
  };

  const handleAssignMember = user => {
    handleAssignOrRemoveMember(user, 'assign');
  };

  const handleRemoveMember = user => {
    handleAssignOrRemoveMember(user, 'remove');
  };

  const onTaskDescriptionChange = e => {
    let errors = { ...saveErrors };
    delete errors['taskDescription'];
    setSaveErrors(errors);
    setTaskDescription(e.target.value);
  };

  const onLocationChange = e => {
    setSelectedLocation(e);
  };

  const onTaskTypeChange = e => {
    setSelectedTaskType(e);
  };

  const popupToggle = () => {
    setPopupOpen(!popupOpen);
  };

  const handleAddCheckList = data => {
    checklistData.push(data);
    setChecklistData([...checklistData]);
  };

  const handleUpdateCheckList = data => {
    const newData = checklistData.map(checklist =>
      checklist.id === data.id ? data : checklist,
    );
    setChecklistData([...newData]);
  };

  const handleDeleteCheckList = id => {
    const newData = checklistData.filter(
      checklistData => checklistData.id !== id,
    );
    setChecklistData([...newData]);
  };

  const handleSave = () => {
    if (checkError()) {
      if (selectedOffice && selectedOffice.value) {
        const start_date = moment(
          startDate.format('YYYY-MM-DD') + ' ' + startTime,
        );
        const end_date = moment(endDate.format('YYYY-MM-DD') + ' ' + endTime);
        let assigneeIds = [];
        if (selectedAssignees && selectedAssignees.length > 0) {
          selectedAssignees.forEach(element => {
            assigneeIds.push(element.id);
          });
        }
        let payload = {
          name: selectedTask?.label,
          public_zone_id: selectedPublicZone.value,
          office_id: selectedOffice.value,
          assignees: assigneeIds,
          description: taskDescription,
          location_id: selectedLocation.value,
          required_start_date: moment(start_date).format(
            'YYYY-MM-DDTHH:mm:ssZ',
          ),
          required_end_date: moment(end_date).format('YYYY-MM-DDTHH:mm:ssZ'),
          sub_tasks: checklistData ? checklistData : [],
        };
        if (
          selectedTask?.value &&
          selectedTask?.value !== selectedTask?.label
        ) {
          payload.master_task_id = selectedTask?.value;
        }
        const officeLocation = officesShortList.find(
          office => office.id === selectedOffice.value,
        ).location;
        if (
          officeLocation.latitude === selectedLocation.latitude &&
          officeLocation.longitude === selectedLocation.longitude &&
          isSameLocationWarningModalOpen === false
        ) {
          setIsSameLocationWarningModalOpen(true);
          return;
        }
        createTask(payload);
        setIsSubmitted(true);
      }
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;
    if (!selectedTask) {
      formIsValid = false;
      saveErrors['selectedTask'] = t('task_name_is_required');
    } else {
      if (!selectedTask.label.trim()) {
        formIsValid = false;
        saveErrors['selectedTask'] = 'Task name is required';
      } else if (checkUrl(selectedTask.label)) {
        formIsValid = false;
        saveErrors['selectedTask'] = 'Task name can not contain url.';
      } else if (checkHtml(selectedTask.label)) {
        formIsValid = false;
        saveErrors['selectedTask'] = 'Invalid Task name';
      } else if (selectedTask.label.length > 255) {
        formIsValid = false;
        saveErrors['selectedTask'] = 'Maximum length 255 characters';
      }
    }
    if (selectedAssignees.length === 0) {
      formIsValid = false;
      saveErrors['selectedAssignees'] = 'Assignee is required';
    }
    if (taskDescription?.length === 0) {
      formIsValid = false;
      saveErrors['taskDescription'] = 'Description is required';
    } else if (checkUrl(taskDescription)) {
      formIsValid = false;
      saveErrors['taskDescription'] = 'Task description can not contain url.';
    } else if (checkHtml(taskDescription)) {
      formIsValid = false;
      saveErrors['taskDescription'] = 'Invalid Task description';
    } else if (taskDescription.length > 512) {
      formIsValid = false;
      saveErrors['taskDescription'] = 'Maximum length 512 characters';
    }
    if (!startDate) {
      formIsValid = false;
    }
    if (!startTime || !endTime) {
      formIsValid = false;
    }
    if (
      selectedTask &&
      selectedTask?.max_task_duration >= 0 &&
      moment(endDate.format('YYYY-MM-DD') + ' ' + endTime).diff(
        moment(startDate.format('YYYY-MM-DD') + ' ' + startTime),
      ) >
        selectedTask?.max_task_duration * 60 * 60 * 1000
    ) {
      formIsValid = false;
      saveErrors['timeDifferenceEndTime'] = `${t('max_task_duration_is')} ${
        selectedTask?.max_task_duration
      } ${t('hours')}`;
    } else if (
      moment(endDate.format('YYYY-MM-DD') + ' ' + endTime).diff(
        moment(startDate.format('YYYY-MM-DD') + ' ' + startTime),
      ) < 1
    ) {
      formIsValid = false;
      saveErrors['timeDifferenceEndTime'] = t(
        'end_time_must_be_after_start_time',
      );
    } else if (
      moment(endDate.format('YYYY-MM-DD') + ' ' + endTime).diff(
        moment(startDate.format('YYYY-MM-DD') + ' ' + startTime),
      ) < 1800000
    ) {
      formIsValid = false;
      saveErrors['timeDifferenceEndTime'] =
        'Minimum 30 minutes time difference required';
    }
    if (
      moment(startDate.format('YYYY-MM-DD') + ' ' + startTime).diff(moment()) <
      1
    ) {
      formIsValid = false;
      saveErrors['timeDifferenceStartTime'] = t('past_time_cant_be_set');
    }
    setSaveErrors(saveErrors);
    return formIsValid;
  };
  return (
    <div className="content">
      <PageTitle>{t('create_task')}</PageTitle>
      <ContentHeader>
        <BackButtonComponent
          onClick={() => history.push('/user/tasks')}
          subTitle={t('back_to_all_tasks')}
        />
      </ContentHeader>
      <Container padding="30px" gap="20px">
        <CommonText name title fontSize="16px">
          {t('add_task_details')}
        </CommonText>
        <InputSection>
          <CreateEditTaskForm
            {...{
              selectedOffice,
              officeOptions,
              officesShortList,
              filterOption,
              selectedTask,
              saveErrors,
              setSaveErrors,
              memberShortListIsLoading,
              selectedAssignees,
              orgMembersList,
              handleAssignMember,
              handleRemoveMember,
              startTime,
              setStartTime,
              startDate,
              setStartDate,
              endTime,
              setEndTime,
              endDate,
              setEndDate,
              selectedLocation,
              taskLocationOptions,
              taskDescription,
              onOfficeChange,
              CreateProjectDropdown,
              onTaskNameChange,
              popupToggle,
              onTaskDescriptionChange,
              onLocationChange,
              publicZoneOptions,
              publicZoneList,
              selectedPublicZone,
              onPublicZoneChange,
              isSameLocationWarningModalOpen,
              popupToggleSameLocationWarningModal,
              handleSave,
              isLoading,
              popupOpen,
              addTaskLocation,
              addTaskLocationLoading,
              createdTaskLocationInfo,
              getTaskLocation,
              getOfficesShortList,
              masterTaskOptions,
              checklistData,
              setChecklistData,
            }}
            creating={true}
          />
          <InputWithLabelSection style={{ gridColumn: `1 / span 2` }}>
            <CheckLists
              updateSubTaskRequest={handleUpdateCheckList}
              checklistData={checklistData}
              deleteCheckList={handleDeleteCheckList}
            />
            <CheckList
              {...{
                newDefaultSubTask,
                isAddDefaultSubTaskLoading,
                addDefaultSubTask,
                selectedPublicZone,
                clearNewDefaultSubTask,
                checklistData,
              }}
              handleAddCheckList={handleAddCheckList}
              defaultCheckListOptions={defaultCheckListOptions}
            />
          </InputWithLabelSection>
          <PrimaryButton
            margin="80px 0 150px 0"
            disabled={
              !selectedTask ||
              !selectedOffice ||
              selectedAssignees.length === 0 ||
              !selectedLocation ||
              !startTime ||
              taskDescription?.length === 0 ||
              !endTime
            }
            onClick={() => handleSave()}
          >
            {isLoading ? (
              <ButtonTextLoader
                loadingText={t('create_task')}
                fontSize="13px"
              />
            ) : (
              t('create_task')
            )}
          </PrimaryButton>
          <div />
        </InputSection>
      </Container>
    </div>
  );
};

export default CreateTask;
